import { useEffect, useState, type ReactNode } from "react";
import { AlarmClock, ArrowDown, ArrowDownToLine, Check, ListChecks, Pause, Play, Star, X } from "lucide-react";
import { PRIORITY_LABEL, type TaskWithFlags } from "@desk/contracts";
import type { TodayNowModel, TodayPullNextModel, TodayScreenProps } from "../types";
import { FocusEmptyState, FocusPageHeader } from "./chrome";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/misc";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { DeadlineLabel, SyncBadge } from "@/components/task-bits";
import { cn } from "@/lib/utils";

/** Thin, quiet progress bar paired with its text ("2 of 5 done"). */
export function Progress({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2.5 text-[12.5px] text-muted-foreground tabular" aria-live="polite">
      <span>
        {done} of {total} done
      </span>
      <span
        role="progressbar"
        aria-label="Today's progress"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        className="focus-bar relative h-[3px] w-20 overflow-hidden rounded-full bg-foreground/[0.09]"
      >
        <span className="absolute inset-y-0 left-0 rounded-full bg-foreground/55" style={{ width: `${pct}%` }} />
      </span>
    </div>
  );
}

/** Checklist you can tick without opening the task. */
function NowChecklist({
  task,
  onToggle,
  interactive,
  square,
}: {
  task: TaskWithFlags;
  onToggle?: TodayNowModel["onToggleChecklistItem"];
  interactive: boolean;
  /** Paper's rounded-square boxes instead of Focus's circles. */
  square?: boolean;
}) {
  if (task.checklist.length === 0) return null;
  const done = task.checklist.filter((c) => c.done).length;
  return (
    <div className="-mx-1.5 mt-4">
      <ul aria-label={`Checklist, ${done} of ${task.checklist.length} done`} className="flex flex-col">
        {task.checklist.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              role="checkbox"
              aria-checked={item.done}
              tabIndex={interactive ? 0 : -1}
              onClick={(e) => {
                e.stopPropagation();
                onToggle?.(item.id, !item.done);
              }}
              className="group/ci flex min-h-8 w-full items-center gap-3 rounded-md px-1.5 py-1 text-left outline-none hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              <span
                aria-hidden
                className={cn(
                  "inline-flex size-[15px] shrink-0 items-center justify-center border-[1.5px] transition-colors duration-150",
                  square ? "rounded-[4px]" : "rounded-full",
                  item.done ? "border-foreground/60 bg-foreground/60 text-background" : "border-foreground/30 group-hover/ci:border-foreground/60",
                )}
              >
                {item.done && <Check className="size-[9px]" strokeWidth={4} />}
              </span>
              <span className={cn("min-w-0 break-words text-[14px] leading-5", item.done ? "text-muted-foreground line-through decoration-foreground/25" : "text-foreground/90")}>
                {item.title}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The card's content; also rendered (inert) as the leaving layer during the swap to the next task. */
function NowBody({
  now,
  p,
  onDone,
  inert,
  variant = "focus",
  renderCheck,
}: {
  now: TodayNowModel;
  p: TodayScreenProps;
  onDone?: () => void;
  inert?: boolean;
  variant?: NowVariant;
  renderCheck?: NowCheck;
}) {
  const t = now.task;
  const inProgress = t.status === "in_progress";
  const live = !inert;
  return (
    <>
      <div className="flex items-start gap-3">
        {renderCheck && <span className="flex h-[34px] shrink-0 items-center">{renderCheck({ onDone: () => onDone?.(), interactive: live, title: t.title })}</span>}
        <h2 className="min-w-0 flex-1 text-[26px] font-semibold leading-[34px] tracking-[-0.022em] text-foreground">
          <button
            type="button"
            tabIndex={live ? 0 : -1}
            onClick={(e) => {
              e.stopPropagation();
              p.onOpenTask(t);
            }}
            className="cursor-default break-words rounded-md text-left outline-none decoration-foreground/20 underline-offset-[5px] hover:underline focus-visible:ring-2 focus-visible:ring-ring/40"
          >
            {t.title}
          </button>
        </h2>
        <div className="-mr-2 mt-1 flex shrink-0 items-center">{live && now.menu}</div>
      </div>
      {/* With a title checkbox, everything below lines up with the title text (Things). */}
      <div className={cn(renderCheck && "pl-[34px]")}>
        <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-[13px] text-muted-foreground">
          <span className="min-w-0 truncate">{now.location}</span>
          {inProgress && (
            <span className="inline-flex items-center gap-1.5 text-foreground/80">
              <StatusIcon status="in_progress" className="size-3.5" />
              In progress
            </span>
          )}
          {t.priority !== "none" && (
            <span className="inline-flex items-center gap-1.5">
              <PriorityIcon priority={t.priority} className="size-3.5" title={false} />
              {PRIORITY_LABEL[t.priority]}
            </span>
          )}
          {t.deadline && <DeadlineLabel deadline={t.deadline} today={p.date} />}
          {t.checklist.length > 0 && (
            <span className="inline-flex items-center gap-1.5 tabular">
              <ListChecks className="size-3.5" aria-hidden />
              {t.checklist.filter((c) => c.done).length} of {t.checklist.length}
            </span>
          )}
          <SyncBadge mark={now.sync} />
          {t.readableId !== "DSK-0" && <span className="ml-auto text-[12px] text-subtle tabular">{t.readableId}</span>}
        </div>
        {t.description && <p className="clamp-2 mt-3 max-w-[60ch] whitespace-pre-line text-[14px] leading-[21px] text-muted-foreground">{t.description}</p>}
        <NowChecklist task={t} onToggle={now.onToggleChecklistItem} interactive={live} square={variant === "paper"} />
        <div className={cn("flex flex-wrap items-center gap-2", renderCheck ? "mt-5" : "mt-6")}>
          {!renderCheck && (
            <Button size="lg" variant="default" tabIndex={live ? 0 : -1} onClick={onDone} className="min-w-[96px]">
              <Check strokeWidth={2.5} />
              Done
            </Button>
          )}
          {inProgress ? (
            <Button size="lg" variant="outline" tabIndex={live ? 0 : -1} onClick={now.onPause}>
              <Pause />
              Pause
            </Button>
          ) : (
            <Button size="lg" variant="outline" tabIndex={live ? 0 : -1} onClick={() => p.onStart(t)}>
              <Play />
              Start
            </Button>
          )}
          {live ? (
            now.snoozeMenu(
              <Button size="lg" variant="ghost" className="text-foreground/80">
                <AlarmClock />
                Snooze
              </Button>,
            )
          ) : (
            <Button size="lg" variant="ghost" tabIndex={-1} className="text-foreground/80">
              <AlarmClock />
              Snooze
            </Button>
          )}
          <Tooltip content="Move it down one">
            <Button size="lg" variant="ghost" tabIndex={live ? 0 : -1} disabled={!now.canSkip} onClick={now.onSkip} className="ml-auto text-foreground/80">
              <ArrowDown />
              Skip
            </Button>
          </Tooltip>
        </div>
      </div>
    </>
  );
}

/**
 * The Now card: the first open planned task, worked one at a time. Done swaps to the next task with
 * a short crossfade; the outgoing layer is inert so the next task is usable immediately.
 */
export type NowVariant = "focus" | "paper";
/** A concept's own completion checkbox for the Now card's title row (replaces the Done button). */
export type NowCheck = (a: { onDone: () => void; interactive: boolean; title: string }) => ReactNode;

export function NowCard({
  now,
  p,
  variant = "focus",
  renderCheck,
}: {
  now: TodayNowModel;
  p: TodayScreenProps;
  variant?: NowVariant;
  renderCheck?: NowCheck;
}) {
  const [leaving, setLeaving] = useState<TodayNowModel | null>(null);
  const t = now.task;
  // Clear the leaving layer if the same task comes back (e.g. Undo).
  useEffect(() => {
    if (leaving && leaving.task.id === t.id) setLeaving(null);
  }, [t.id, leaving]);
  return (
    <article
      {...now.rowProps}
      aria-label={`Now: ${t.title}`}
      onClick={(e) => {
        now.rowProps.onClick?.(e);
        // Card actions act on the task without moving the keyboard cursor onto it.
        if (!(e.target as HTMLElement).closest("button, a, [role=menu]")) now.onSelect();
      }}
      onDoubleClick={() => p.onOpenTask(t)}
      className={cn(
        "relative overflow-hidden border bg-card outline-none transition-[border-color,box-shadow] duration-150",
        variant === "paper"
          ? "rounded-xl px-6 pb-5 pt-5 shadow-[0_1px_2px_rgba(37,36,34,0.05),0_8px_24px_-16px_rgba(37,36,34,0.18)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]"
          : "rounded-2xl px-7 pb-6 pt-6 shadow-[0_1px_2px_rgba(37,36,34,0.04),0_12px_32px_-18px_rgba(37,36,34,0.22)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.3),0_16px_40px_-20px_rgba(0,0,0,0.7)]",
        now.selected ? "border-foreground/30" : "border-border",
        "focus-visible:ring-2 focus-visible:ring-ring/40",
      )}
    >
      {leaving && (
        <div
          aria-hidden
          className={cn("focus-now-out pointer-events-none absolute", variant === "paper" ? "inset-x-6 top-5" : "inset-x-7 top-6")}
          onAnimationEnd={() => setLeaving(null)}
        >
          <NowBody now={leaving} p={p} inert variant={variant} renderCheck={renderCheck} />
        </div>
      )}
      <div key={t.id} className={leaving ? "focus-now-in-late" : undefined}>
        <NowBody
          now={now}
          p={p}
          variant={variant}
          renderCheck={renderCheck}
          onDone={() => {
            setLeaving(now);
            p.onComplete(t);
          }}
        />
      </div>
    </article>
  );
}

/** Suggests the best unplanned task from the focus; adds it only when confirmed. */
export function PullNext({ model, focusLabel, today }: { model: TodayPullNextModel; focusLabel: string | null; today: string }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const list = model.candidates;
  const pick = list.length ? list[i % list.length]! : null;
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => (setI(0), setOpen(true))}
        className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] text-muted-foreground outline-none hover:bg-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        <ArrowDownToLine className="size-3.5" />
        Pull next
      </button>
    );
  }
  return (
    <div role="group" aria-label="Pull next" className="flex min-h-[52px] w-full items-center gap-3 rounded-xl border border-dashed border-foreground/20 py-2 pl-3.5 pr-2">
      {pick ? (
        <>
          <span className="inline-flex w-4 justify-center">{pick.task.priority !== "none" && <PriorityIcon priority={pick.task.priority} className="size-3.5" />}</span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] text-foreground" title={pick.task.title}>
              {pick.task.title}
            </p>
            <p className="flex min-w-0 items-center gap-2.5 text-[12.5px] text-subtle">
              <span className="truncate">{pick.location}</span>
              {pick.task.deadline && <DeadlineLabel deadline={pick.task.deadline} today={today} className="text-[12px]" />}
            </p>
          </div>
          {list.length > 1 && (
            <Button size="sm" variant="ghost" onClick={() => setI(i + 1)}>
              Another
            </Button>
          )}
          <Button
            size="sm"
            variant="default"
            onClick={() => {
              model.onAdd(pick.task);
              setOpen(false);
            }}
          >
            Add to Today
          </Button>
        </>
      ) : (
        <p className="flex-1 text-[13px] text-muted-foreground">
          {model.loading ? "Looking…" : focusLabel ? `Nothing else open in ${focusLabel}.` : "Nothing else open."}
        </p>
      )}
      <Tooltip content="Close">
        <Button size="icon-sm" variant="quiet" aria-label="Close Pull next" onClick={() => setOpen(false)}>
          <X />
        </Button>
      </Tooltip>
    </div>
  );
}

export function FocusTodayScreen(p: TodayScreenProps) {
  const { progress, slots, now, pullNext } = p;
  const featured = now !== undefined; // the feature layer supplies Now wiring
  const nothingPlanned = progress.total === 0;
  return (
    <div>
      <FocusPageHeader
        title="Today"
        icon={<Star className="fill-[var(--star)] text-[var(--star)]" />}
        actions={
          <>
            {slots.modeToggle}
            {!nothingPlanned && slots.addTasks}
          </>
        }
        below={<div className="flex flex-wrap items-center gap-2">{slots.focusChip}</div>}
      />
      {slots.carryover && <div className="-mt-2 mb-3">{slots.carryover}</div>}

      {!featured ? (
        slots.plannedList
      ) : now ? (
        <NowCard now={now} p={p} />
      ) : (
        <FocusEmptyState
          message={
            nothingPlanned
              ? "Nothing planned"
              : p.focusLabel && p.planned.length === 0 && progress.done < progress.total
                ? `Nothing planned for ${p.focusLabel}`
                : "Everything planned is done"
          }
          action={nothingPlanned || !pullNext ? slots.addTasks : <PullNext model={pullNext} focusLabel={p.focusLabel} today={p.date} />}
        />
      )}

      {featured && now && (
        <section aria-label="Up next" className={p.planned.length > 1 ? "mt-8" : "mt-4"}>
          <div className="mb-1 flex h-8 items-center gap-3 px-2.5">
            {p.planned.length > 1 && <h2 className="font-sans text-[13px] font-semibold tracking-normal text-foreground/80">Up next</h2>}
            <div className="ml-auto">
              <Progress done={progress.done} total={progress.total} />
            </div>
          </div>
          {slots.upNextList}
          {pullNext && (
            <div className="mt-2">
              <PullNext model={pullNext} focusLabel={p.focusLabel} today={p.date} />
            </div>
          )}
        </section>
      )}
      {featured && !now && !nothingPlanned && (
        <div className="-mt-5 flex h-8 items-center px-2.5">
          <Progress done={progress.done} total={progress.total} />
        </div>
      )}

      {(p.due.length > 0 || slots.dueOutside) && (
        <div className="mt-8">
          {p.due.length > 0 && slots.dueList}
          {slots.dueOutside && <div className="mt-1">{slots.dueOutside}</div>}
        </div>
      )}
      {slots.completedList && <div className="mt-6">{slots.completedList}</div>}
    </div>
  );
}
